import { Button, Menu } from '@ui/themed';
import { t } from 'i18next';
import { createContext, type PropsWithChildren, type ReactNode, useContext } from 'react';
import { cn } from '../../../lib/styles';
import { nextSelection, splitRowActions, treeIndentClass } from './data-table-utils';
import type {
  DataTableCellProps,
  DataTableColumn,
  DataTableColumnWidth,
  DataTableOption,
  DataTableRowAction,
  DataTableRowModel,
} from './types';

type TreeCellProps<TData> = PropsWithChildren<{
  row: DataTableRowModel<TData>;
  chevron: ReactNode;
  connector: ReactNode;
  expanderClassName?: string;
  connectorClassName?: string;
}>;

type TreeExpanderProps = {
  expanded: boolean;
  onToggle: () => void;
  icon: ReactNode;
  className?: string;
};

type FilterPillsProps = {
  options: readonly DataTableOption[];
  selected?: readonly string[];
  setSelected?: (values: string[]) => void;
  multiple?: boolean;
  activeClassName?: string;
};

const TreeExpanderContext = createContext<TreeExpanderProps | undefined>(undefined);

export function TreeExpanderSlot() {
  const expander = useContext(TreeExpanderContext);
  return expander ? <TreeExpander {...expander} /> : null;
}

function TreeExpander({ expanded, onToggle, icon, className }: TreeExpanderProps) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      aria-label={expanded ? t('tables:tree.collapse') : t('tables:tree.expand')}
      aria-expanded={expanded}
      className={cn(
        'flex size-5 shrink-0 cursor-pointer items-center justify-center rounded font-semibold hover:bg-spark-surface-hover',
        className,
      )}
    >
      <span
        className={cn(
          'inline-flex transition-transform ease-out',
          !expanded && '-rotate-90 rtl:rotate-90',
        )}
      >
        {icon}
      </span>
    </button>
  );
}

export function TreeCell<TData>({
  row,
  children,
  chevron,
  connector,
  expanderClassName,
  connectorClassName,
}: TreeCellProps<TData>) {
  if (row.hasChildren) {
    const onToggle = row.onToggleExpand;
    return (
      <TreeExpanderContext.Provider
        value={
          onToggle
            ? { expanded: row.expanded, onToggle, icon: chevron, className: expanderClassName }
            : undefined
        }
      >
        <span className={cn('flex items-start', treeIndentClass(row.depth))}>{children}</span>
      </TreeExpanderContext.Provider>
    );
  }

  return (
    <span className={cn('flex items-center gap-2', treeIndentClass(row.depth))}>
      {row.depth > 0 && (
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex size-5 shrink-0 items-center justify-center',
            connectorClassName,
          )}
        >
          {connector}
        </span>
      )}
      {children}
    </span>
  );
}

export function rowClickHandler<TData>(
  row: DataTableRowModel<TData>,
  onRowClick?: (row: TData) => void,
) {
  if (row.onToggleExpand) return row.onToggleExpand;
  return onRowClick ? () => onRowClick(row.data) : undefined;
}

const MAX_WIDTH_CLASS: Record<Exclude<DataTableColumnWidth, 'none'>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

export function CellContent({
  column,
  children,
}: PropsWithChildren<{ column: Pick<DataTableColumn, 'maxWidth' | 'sticky' | 'interactive'> }>) {
  const width = column.maxWidth ?? (column.sticky || column.interactive ? 'none' : 'md');
  if (width === 'none') return <>{children}</>;

  return (
    <span
      className={cn('-m-1 block min-w-0 overflow-hidden text-ellipsis p-1', MAX_WIDTH_CLASS[width])}
    >
      {children}
    </span>
  );
}

export function InteractiveCell({ children }: PropsWithChildren) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: guards propagation only — the controls inside own the interaction
    <span className="contents" onClick={(event) => event.stopPropagation()}>
      {children}
    </span>
  );
}

export function FilterPills({
  options,
  selected = [],
  setSelected,
  multiple,
  activeClassName = 'border-spark-border-strong bg-spark-surface-muted text-spark-text-primary',
}: FilterPillsProps) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => setSelected?.(nextSelection(selected, option.id, multiple))}
          aria-pressed={selected.includes(option.id)}
          className={cn(
            'cursor-pointer rounded-full border px-2 py-0.5 text-xs transition',
            selected.includes(option.id)
              ? activeClassName
              : 'border-spark-border-default text-spark-text-secondary hover:bg-spark-surface-hover',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** A cell's inner layout; platforms differ only in the `gap`. */
export function CellBase({ children, align, gap }: DataTableCellProps & { gap: string }) {
  return (
    <span className={cn('flex items-center', gap, align === 'end' && 'justify-end')}>
      {children}
    </span>
  );
}

/**
 * Row actions, with the overflow folded into a "More actions" menu. Wix supplies its own,
 * built on `TableActionCell`.
 */
export function RowActions({ actions: allActions }: { actions: DataTableRowAction[] }) {
  const { actions, moreActions } = splitRowActions(allActions);
  return (
    <>
      {actions.map((action) => (
        <Button
          key={action.id ?? action.label}
          variant={action.danger ? 'dangerOutline' : (action.variant ?? 'secondary')}
          icon={action.icon}
          disabled={action.disabled}
          collapseLabel={action.collapseLabel ?? 'mobile'}
          onClick={action.onClick}
          ariaLabel={action.label}
        >
          {action.label}
        </Button>
      ))}
      {moreActions.length > 0 && (
        <Menu
          collapsible
          align="end"
          buttonText={t('buttons:more_actions')}
          items={moreActions.map((action) => ({
            key: action.id ?? action.label,
            label: action.label,
            icon: action.icon,
            disabled: action.disabled,
            danger: action.danger,
            onClick: action.onClick,
          }))}
        />
      )}
    </>
  );
}
